import React, { useMemo } from 'react';
import {
  Button,
  Heading,
  Body,
  MonoLabel,
  Card,
} from '@/components/rc';
import type { DuplicateGroup } from '@/utils/importDedup';
import { cn } from '@/lib/utils';

// ── Types ──

interface DuplicateReviewProps {
  groups: DuplicateGroup[];
  onConfirm: (resolved: DuplicateGroup[]) => void;
  onToggle: (index: number, merged: boolean) => void;
  onBulkMerge: () => void;
  onBulkKeep: () => void;
}

// ── Component ──

export default function DuplicateReview({
  groups,
  onConfirm,
  onToggle,
  onBulkMerge,
  onBulkKeep,
}: DuplicateReviewProps) {
  const mergeCount = groups.filter(g => g.merged).length;
  const totalBefore = groups.reduce((acc, g) => acc + g.rowIndices.length, 0);
  const totalAfter = groups.reduce(
    (acc, g) => acc + (g.merged ? 1 : g.rowIndices.length),
    0,
  );

  const pluralS = groups.length === 1 ? '' : 's';

  // Tri-state: 'all-separate' | 'mixed' | 'all-merged'
  const bulkState =
    mergeCount === 0
      ? 'all-separate'
      : mergeCount === groups.length
        ? 'all-merged'
        : 'mixed';

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="mb-2">
        <Heading scale="heading">Review Duplicates</Heading>
      </div>
      <Body size="caption" colour="secondary" className="w-auto mb-4">
        {groups.length} wine{pluralS} appear{groups.length === 1 ? 's' : ''} more than once in your file.
        Merged entries will combine bottle counts.
      </Body>

      {/* Tri-state bulk toggle */}
      <div className="flex items-center justify-center gap-3 mb-4">
        <button
          onClick={onBulkKeep}
          className={cn(
            'font-[var(--rc-font-mono)] text-[11px] uppercase tracking-wider transition-colors',
            bulkState === 'all-separate'
              ? 'text-[var(--rc-ink-primary)]'
              : 'text-[var(--rc-ink-ghost)] hover:text-[var(--rc-accent-pink)]',
          )}
        >
          Keep separate
        </button>
        <div
          role="slider"
          tabIndex={0}
          aria-label="Bulk merge toggle"
          aria-valuemin={0}
          aria-valuemax={2}
          aria-valuenow={bulkState === 'all-separate' ? 0 : bulkState === 'mixed' ? 1 : 2}
          aria-valuetext={bulkState === 'all-separate' ? 'All kept separate' : bulkState === 'mixed' ? 'Mixed' : 'All merged'}
          className="relative w-[56px] h-[28px] rounded-full cursor-pointer transition-colors duration-200 bg-[var(--rc-surface-tertiary)]"
          onClick={() => {
            if (bulkState === 'all-merged') {
              onBulkKeep();
            } else {
              onBulkMerge();
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
              e.preventDefault();
              onBulkMerge();
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
              e.preventDefault();
              onBulkKeep();
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              if (bulkState === 'all-merged') onBulkKeep();
              else onBulkMerge();
            }
          }}
        >
          {/* Track fill — partial pink when mixed, full pink when all merged */}
          <div
            className={cn(
              'absolute inset-0 rounded-full transition-all duration-200',
              bulkState === 'all-merged' && 'bg-[var(--rc-accent-pink)]',
            )}
            style={bulkState === 'mixed' ? { backgroundColor: 'var(--rc-accent-pink)', opacity: 0.3 } : undefined}
          />
          {/* Knob */}
          <div
            className={cn(
              'absolute top-[4px] w-5 h-5 bg-white rounded-full shadow-sm transition-all duration-200',
              bulkState === 'all-separate' && 'left-[4px]',
              bulkState === 'mixed' && 'left-[18px]',
              bulkState === 'all-merged' && 'left-[32px]',
            )}
          />
        </div>
        <button
          onClick={onBulkMerge}
          className={cn(
            'font-[var(--rc-font-mono)] text-[11px] uppercase tracking-wider transition-colors',
            bulkState === 'all-merged'
              ? 'text-[var(--rc-accent-pink)]'
              : 'text-[var(--rc-ink-ghost)] hover:text-[var(--rc-accent-pink)]',
          )}
        >
          Merge all
        </button>
      </div>

      {/* Group cards — scrollable area */}
      <div className="flex-1 overflow-y-auto space-y-3 pb-24">
        {groups.map((group, idx) => (
          <GroupCard
            key={group.key}
            group={group}
            index={idx}
            onToggle={onToggle}
          />
        ))}
      </div>

      {/* Sticky footer */}
      <div className="sticky bottom-0 bg-[var(--rc-surface-primary)] border-t border-[var(--rc-border-subtle)] pt-3 -mx-6 px-6 pb-1">
        <MonoLabel size="label" colour="ghost" className="w-auto block mb-2">
          {mergeCount} merged &middot; {totalBefore} entries &rarr; {totalAfter} cellar{' '}
          {totalAfter === 1 ? 'entry' : 'entries'}
        </MonoLabel>
        <Body size="caption" colour="ghost" className="w-auto mb-3">
          Merged entries combine bottle counts into a single cellar entry
        </Body>
        <Button
          variantType="Primary"
          label="Continue"
          onClick={() => onConfirm(groups)}
          className="w-full"
        />
      </div>
    </div>
  );
}

// ── Group Card ──

function GroupCard({
  group,
  index,
  onToggle,
}: {
  group: DuplicateGroup;
  index: number;
  onToggle: (index: number, merged: boolean) => void;
}) {
  const { label, rowIndices, totalQuantity, merged, differingFields } = group;

  const subtitle = [label.vintage, label.name, label.cepage]
    .filter(Boolean)
    .join(' \u00B7 ');

  return (
    <Card
      elevation="flat"
      padding="standard"
      className={cn(
        'border border-[var(--rc-border-subtle)] transition-colors duration-200',
        merged
          ? 'border-l-2 border-l-[var(--rc-accent-pink)]'
          : 'border-l-2 border-l-transparent',
      )}
    >
      {/* Wine identity */}
      <Body weight="medium" className="w-auto">
        {label.producer || label.name}
      </Body>
      {subtitle && (
        <MonoLabel size="micro" colour="ghost" className="w-auto mt-0.5">
          {subtitle}
        </MonoLabel>
      )}

      {/* Entry count + total qty */}
      <MonoLabel size="label" className="w-auto mt-2 block">
        {rowIndices.length} entries &rarr; {totalQuantity} bottle{totalQuantity !== 1 ? 's' : ''}
      </MonoLabel>

      {/* Context line */}
      {merged && differingFields.length > 0 && (
        <Body size="caption" colour="ghost" className="w-auto mt-1">
          Keeping first entry's data &middot; {differingFields.join(', ')} from other rows discarded
        </Body>
      )}
      {!merged && (
        <Body size="caption" colour="secondary" className="w-auto mt-1">
          You'll have {rowIndices.length} separate entries for this wine
        </Body>
      )}

      {/* Toggle: Merge */}
      <div className="flex items-center justify-between mt-3">
        <MonoLabel size="label" colour={merged ? 'accent-pink' : 'ghost'}>
          Merge
        </MonoLabel>
        <div
          role="switch"
          tabIndex={0}
          aria-checked={merged}
          onClick={() => onToggle(index, !merged)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle(index, !merged);
            }
          }}
          className={cn(
            'relative w-[44px] h-[24px] rounded-full transition-colors duration-200 cursor-pointer',
            merged ? 'bg-[var(--rc-accent-pink)]' : 'bg-[var(--rc-switch-track-off,#555)]',
          )}
        >
          <div
            className={cn(
              'absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform duration-200 shadow-sm',
              merged && 'translate-x-5',
            )}
          />
        </div>
      </div>
    </Card>
  );
}
